import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
} from "react-icons/fa";

interface CheckoutFormProps {
  onSuccess: () => void;
  onError: (message: string) => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ onSuccess, onError }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    // Retrieve the PaymentIntent client secret from the URL if needed
    // This is useful if the user navigates away and comes back
    // const clientSecret = new URLSearchParams(window.location.search).get(
    //   "payment_intent_client_secret"
    // );

    // if (!clientSecret) {
    //   return;
    // }

    // stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
    //   switch (paymentIntent?.status) {
    //     case "succeeded":
    //       setMessage("Payment succeeded!");
    //       onSuccess(); // Call parent success handler
    //       break;
    //     case "processing":
    //       setMessage("Your payment is processing.");
    //       break;
    //     case "requires_payment_method":
    //       setMessage("Your payment was not successful, please try again.");
    //       onError('Payment failed: Requires payment method'); // Call parent error handler
    //       break;
    //     default:
    //       setMessage("Something went wrong.");
    //       onError('Payment failed: Unknown status'); // Call parent error handler
    //       break;
    //   }
    // });
  }, [stripe, onSuccess, onError]);

  window.trackEvent?.("InitiateCheckout");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      console.error("Stripe.js has not loaded yet.");
      return;
    }

    setIsLoading(true);
    setMessage(null); // Clear previous messages
    setIsError(false);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        // We handle success/error client-side with callbacks, but Stripe might still use this
        return_url: `${window.location.origin}/purchase-status`, // Use a dedicated status page potentially
      },
      redirect: "if_required", // Prevent immediate redirect
    });

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Otherwise, your customer will be redirected to
      // your `return_url`. For example, error fields currently handling validation errors
      const userMessage =
        error.message || t("purchasePage.errors.paymentFailed");
      if (error.type === "card_error" || error.type === "validation_error") {
        setMessage(userMessage);
      } else {
        setMessage(
          t("purchasePage.errors.paymentFailed") + `: ${error.message}`
        ); // More generic for unexpected errors
      }
      setIsError(true);
      onError(userMessage); // Notify parent component
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      // Payment succeeded, notify parent component
      setMessage(t("purchasePage.paymentSuccess", "Payment successful!")); // Add success message translation key
      setIsError(false);
      onSuccess();
    } else if (paymentIntent) {
      // Handle other statuses like processing, requires_action, etc.
      setMessage(
        t(
          "purchasePage.paymentProcessing",
          `Payment status: ${paymentIntent.status}`
        )
      );
      setIsError(false); // Usually not an error state
      // Potentially trigger specific UI updates based on status
      console.log("PaymentIntent status:", paymentIntent.status);
    } else {
      // Handle cases where paymentIntent is unexpectedly null after success/no error
      setMessage(
        t(
          "purchasePage.errors.unexpected",
          "An unexpected issue occurred. Please check your payment status or contact support."
        )
      );
      setIsError(true);
      onError("Unexpected issue after payment attempt");
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: "tabs" as const, // Use "tabs" or "accordion"
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement
        id="payment-element"
        options={paymentElementOptions}
        className="mb-4"
      />
      <button
        disabled={isLoading || !stripe || !elements}
        id="submit"
        className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-700 hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: "linear-gradient(to right, #10B981, #059669)",
          cursor: "pointer",
        }}
      >
        {isLoading ? (
          <FaSpinner className="animate-spin h-5 w-5 text-white" />
        ) : (
          <div className="flex items-center text-white text-xl">
            {t("purchasePage.payButton", "Pay Now")}
          </div>
        )}
      </button>
      {/* Show any error or success messages */}
      {message && (
        <div
          id="payment-message"
          className={`flex items-center p-4 rounded-md text-sm ${
            isError
              ? "bg-red-50 border border-red-200 text-red-700"
              : "bg-green-50 border border-green-200 text-green-700"
          }`}
          role="alert"
        >
          {isError ? (
            <FaExclamationTriangle
              className="h-5 w-5 mr-3 flex-shrink-0"
              aria-hidden="true"
            />
          ) : (
            <FaCheckCircle
              className="h-5 w-5 mr-3 flex-shrink-0"
              aria-hidden="true"
            />
          )}
          <span>{message}</span>
        </div>
      )}
    </form>
  );
};

export default CheckoutForm;
